import useAnimatedHeight from '@/hooks/useAnimatedHeight/useAnimatedHeight';
import FormField from '@/uikit/components/FormField/FormField';
import LinkButton from '@/uikit/components/LinkButton/LinkButton';
import { animated, useTransition } from '@react-spring/web';
import FormRow from '../FormRow/FormRow';
import Icon from '../Icon/Icon';
import styles from './CourseMembersForm.module.scss';
import type { CourseMember, CourseMembersFormProps } from './types';

const CourseMembersForm = ({
  members,
  createNewMember,
  getFieldProps,
  removeMember,
}: CourseMembersFormProps) => {
  const [animatedHeightStyle, childElementRef] = useAnimatedHeight();

  const membersTransition = useTransition(members, {
    from: {
      opacity: 0,
      position: 'relative',
      transform: 'translateY(-100%)',
      duration: 400,
    },
    enter: {
      opacity: 1,
      position: 'relative',
      transform: 'translateY(0)',
    },
    leave: {
      opacity: 0,
      transform: 'translateY(0)',
      position: 'absolute',
    },
    keys: (member: CourseMember) => member.id,
    exitBeforeEnter: true,
  } as const);

  return (
    <div className={styles.base}>
      <animated.div style={animatedHeightStyle}>
        <div ref={childElementRef} className={styles.rows}>
          {membersTransition((styleProps, member, _, memberIndex) => {
            return (
              <animated.div
                key={member.id}
                style={styleProps}
                className={styles.row}
              >
                <FormRow bottomMarginSize={{ default: 0.75 }}>
                  <FormField
                    label="Vorname"
                    {...getFieldProps(memberIndex, 'firstName')}
                    autoFocus={memberIndex === members.length - 1}
                    errorMessageHasRelativePosition
                  />
                  <FormField
                    label="Nachname"
                    {...getFieldProps(memberIndex, 'lastName')}
                    errorMessageHasRelativePosition
                  />
                  <FormField
                    label="E-Mail"
                    {...getFieldProps(memberIndex, 'email')}
                    errorMessageHasRelativePosition
                  />
                </FormRow>

                <button
                  className={styles.removeButton}
                  onClick={removeMember(member.id)}
                  disabled={members.length <= 1}
                  aria-label="remove"
                >
                  <Icon variant="user/trash" size="iconSize24" />
                </button>
              </animated.div>
            );
          })}
        </div>
      </animated.div>

      <LinkButton
        variant="default"
        icon="action/plus"
        iconPosition="left"
        onClick={createNewMember}
        as="button"
        size="medium"
        disableTransition
        align="left"
      >
        Teilnehmer:in hinzufügen
      </LinkButton>
    </div>
  );
};

export default CourseMembersForm;
